import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Stack,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
const TIMEZONE = "America/St_Johns";

const BookingConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const booking = location.state?.booking;
  const [showDetails, setShowDetails] = useState(false);

  if (!booking) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Booking not found.
        </Typography>
        <Button onClick={() => navigate("/birthday-parties")} sx={{ mt: 2 }}>
          Back to Party Page
        </Button>
      </Box>
    );
  }

  const {
    bookingNumber,
    package: pkg,
    options = {},
    date,
    timeSlot,
    contact,
    pricing = {},
  } = booking;

  const formattedDate = dayjs
    .utc(date)
    .tz(TIMEZONE)
    .format("dddd, MMMM D, YYYY");

  return (
    <Box sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ maxWidth: 600, mx: "auto", p: 4 }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
          <Typography variant="h5" fontWeight="bold">
            Booking Confirmed!
          </Typography>
          <Typography variant="body1">
            Thank you {contact.firstName}! We received your booking.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Booking #: <strong>{bookingNumber}</strong>
          </Typography>

          <Divider sx={{ my: 2, width: "100%" }} />

          <Typography variant="subtitle1">
            Package: {pkg.toUpperCase()} <br />
            {formattedDate} @ {timeSlot}
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Total Paid (via e-Transfer):
          </Typography>
          <Typography fontSize={20} fontWeight={600}>
            ${pricing.total?.toFixed(2)} (incl. tax)
          </Typography>

          <Button
            variant="text"
            size="small"
            endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowDetails((prev) => !prev)}>
            {showDetails ? "Hide Details" : "View Booking Details"}
          </Button>

          <Collapse in={showDetails} sx={{ width: "100%" }}>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Full Name"
                  secondary={`${contact.firstName} ${contact.lastName}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Email" secondary={contact.email} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Phone" secondary={contact.phone} />
              </ListItem>
              <Divider sx={{ my: 1 }} />
              {pkg === "standard" && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Kids Attending"
                      secondary={options.numKids || 12}
                    />
                  </ListItem>
                  {Object.entries(options)
                    .filter(([k, v]) => k !== "numKids" && v)
                    .map(([k, v]) => (
                      <ListItem key={k}>
                        <ListItemText
                          primary={k.replace(/([A-Z])/g, " $1")}
                          secondary="Included"
                        />
                      </ListItem>
                    ))}
                </>
              )}
              {pkg === "premium" && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Parent Treat"
                      secondary={options.parentTreat}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Cutlery Color"
                      secondary={options.cutleryColor}
                    />
                  </ListItem>
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <ListItem>
                <ListItemText
                  primary="Subtotal"
                  secondary={`$${pricing.subtotal?.toFixed(2)}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Tax (15%)"
                  secondary={`$${pricing.tax?.toFixed(2)}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Total"
                  secondary={`$${pricing.total?.toFixed(2)}`}
                />
              </ListItem>
            </List>
          </Collapse>

          <Button variant="contained" onClick={() => navigate("/")}>
            Return Home
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default BookingConfirmationPage;
